import type { I18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import { CircleCheck } from 'lucide-react';
import { useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { z } from 'zod';

import { isValidCep } from '@/shared/format/cep';
import { lookupCep } from '@/shared/http';
import { MaskedField } from '@/shared/ui/masked-field';

import { Bound, SelectFormField, TextFormField } from './fields';

/** Os 27 UFs; rótulo = valor = sigla, como o select do legado. */
export const UFS = ['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'] as const;

/** Endereço no formulário: tudo texto (CEP cru, U37-8); o mapper converte o número e mascara o CEP (U21). */
export interface AddressValues {
  zipCode: string;
  addressName: string;
  number: string;
  district: string;
  city: string;
  uf: string;
  complement: string;
  [field: string]: unknown;
}

export interface AddressOptions {
  /** `all` (Cessionários, pessoa, pagador), `none` ou `allOrNothing` (solicitação de conta), 06 P14. */
  readonly required?: 'all' | 'none' | 'allOrNothing';
  /** `number`: o legado validava como número (Cessionários); `string`: aceita "S/N". */
  readonly numberAs?: 'number' | 'string';
}

export const ADDRESS_EMPTY: AddressValues = { zipCode: '', addressName: '', number: '', district: '', city: '', uf: '', complement: '' };

const text = z.preprocess((value) => (value === null || value === undefined ? '' : typeof value === 'number' ? String(value) : value), z.string());

/** Schema do endereço (P14) com as mensagens do legado (`FundSchema.ts`; U20-5 na caixa). */
export function addressSchema(i18n: I18n, options: AddressOptions = {}): z.ZodType<AddressValues, AddressValues> {
  const mode = options.required ?? 'all';
  const numeric = (options.numberAs ?? 'number') === 'number';
  const messages = {
    zipCode: i18n._(msg`CEP: precisa ser preenchido.`),
    zipCodeInvalid: i18n._(msg`CEP inválido.`),
    addressName: i18n._(msg`Endereço: precisa ser preenchido.`),
    number: i18n._(msg`Número: precisa ser preenchido.`),
    numberInvalid: i18n._(msg`Valor precisa ser um número.`),
    district: i18n._(msg`Bairro: precisa ser preenchido.`),
    city: i18n._(msg`Cidade: precisa ser preenchida.`),
    uf: i18n._(msg`UF: precisa ser preenchida.`),
  };
  const required = ['zipCode', 'addressName', 'number', 'district', 'city', 'uf'] as const;
  return z
    .looseObject({ zipCode: text, addressName: text, number: text, district: text, city: text, uf: text, complement: text })
    .superRefine((value, context) => {
      const filled = Object.values(value).some((item) => typeof item === 'string' && item.trim() !== '');
      const enforce = mode === 'all' || (mode === 'allOrNothing' && filled);
      if (enforce) {
        for (const key of required) if (value[key].trim() === '') context.addIssue({ code: 'custom', path: [key], message: messages[key] });
      }
      if (value.zipCode !== '' && !isValidCep(value.zipCode)) context.addIssue({ code: 'custom', path: ['zipCode'], message: messages.zipCodeInvalid });
      if (numeric && value.number.trim() !== '' && !/^\d+$/.test(value.number.trim())) context.addIssue({ code: 'custom', path: ['number'], message: messages.numberInvalid });
    }) as unknown as z.ZodType<AddressValues, AddressValues>;
}

type Lookup = { readonly state: 'idle' } | { readonly state: 'loading' } | { readonly state: 'found' } | { readonly state: 'missing' } | { readonly state: 'failed' };

export interface AddressFieldsProps {
  /** Onde o endereço mora no formulário (`address`); vazio = raiz. */
  readonly prefix?: string;
  /** Consulta o ViaCEP ao completar o CEP (padrão: sim). */
  readonly cepLookup?: boolean;
  readonly required?: boolean;
}

/**
 * Campos de endereço (P14, 06 §3.d): CEP com o ViaCEP (preenche endereço, bairro, cidade e UF e leva
 * ao número), número, complemento e UF. O registro carregado não reconsulta o CEP.
 */
export function AddressFields({ prefix = 'address', cepLookup = true, required = true }: AddressFieldsProps) {
  const { t } = useLingui();
  const form = useFormContext();
  const path = (key: keyof AddressValues & string) => (prefix ? `${prefix}.${key}` : key);
  const [lookup, setLookup] = useState<Lookup>({ state: 'idle' });
  const initial = useRef<unknown>(form.getValues(path('zipCode')));
  const aborter = useRef<AbortController | null>(null);

  const fill = async (cep: string) => {
    aborter.current?.abort();
    const controller = new AbortController();
    aborter.current = controller;
    setLookup({ state: 'loading' });
    try {
      const address = await lookupCep(cep, controller.signal);
      if (controller.signal.aborted) return;
      if (!address) {
        setLookup({ state: 'missing' });
        return;
      }
      const options = { shouldDirty: true, shouldValidate: true } as const;
      form.setValue(path('addressName'), address.addressName, options);
      form.setValue(path('district'), address.district, options);
      form.setValue(path('city'), address.city, options);
      form.setValue(path('uf'), address.uf, options);
      setLookup({ state: 'found' });
      form.setFocus(path('number'));
    } catch {
      if (!controller.signal.aborted) setLookup({ state: 'failed' });
    }
  };

  const hint =
    lookup.state === 'loading'
      ? t`Buscando endereço…`
      : lookup.state === 'found'
        ? t`Endereço encontrado. Confira o número.`
        : lookup.state === 'missing'
          ? t`CEP não encontrado. Preencha o endereço.`
          : lookup.state === 'failed'
            ? t`Não foi possível buscar o CEP agora. Preencha o endereço.`
            : cepLookup
              ? t`Digite o CEP e o endereço é preenchido sozinho.`
              : undefined;

  return (
    <>
      <Bound props={{ name: path('zipCode'), label: t`CEP`, required, ...(hint ? { hint } : {}) }}>
        {(control, { field }) => (
          <div className="relative">
            <MaskedField
              {...control}
              kind="cep"
              value={typeof field.value === 'string' ? field.value : ''}
              onChange={(raw) => {
                field.onChange(raw);
                if (!cepLookup) return;
                if (raw.length === 8 && raw !== initial.current) {
                  initial.current = raw;
                  void fill(raw);
                } else if (raw.length < 8) {
                  initial.current = undefined;
                  setLookup({ state: 'idle' });
                }
              }}
              onBlur={field.onBlur}
            />
            {lookup.state === 'found' ? <CircleCheck aria-hidden className="pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2 text-ok" /> : null}
          </div>
        )}
      </Bound>
      <TextFormField name={path('addressName')} label={t`Endereço`} required={required} autoComplete="address-line1" className="lg:col-span-2" />
      <TextFormField name={path('number')} label={t`Número`} required={required} inputMode="numeric" />
      <TextFormField name={path('district')} label={t`Bairro`} required={required} />
      <TextFormField name={path('city')} label={t`Cidade`} required={required} autoComplete="address-level2" />
      <SelectFormField name={path('uf')} label={t`UF`} required={required} options={UFS.map((uf) => ({ value: uf, label: uf }))} />
      <TextFormField name={path('complement')} label={t`Complemento`} autoComplete="address-line2" className="lg:col-span-2" />
    </>
  );
}
